import subscribeNotificationPrefs from './subscribeNotificationPrefs';
import NotificationPresets from './NotificationPresets';
import useNotificationContext from './useNotificationContext';
import PastEventChangesSetting from './PastEventChangesSetting';
import ParticipationRequestContent from './ParticipationRequestContent';
import EventNotificationContent from './EventNotificationContent';
import CalendarCoverageSetting from "./CalendarCoverageSetting";
import WeeklyDigestContent from './WeeklyDigestContent';
import SidebarSvgImage from '../core-components/SidebarSvgImage';
// src/components/Notifications/NotificationsDropdown.js
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from '../../Security/firebase-config';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import CustomAlert from '../core-components/CustomAlert';
import './NotificationsDropdown.css';
import { useTeam } from '../../context/TeamContext';

import { NOTIF_TYPES, defaultPrefs, isEnabled, normalizePrefs, serializePrefs, overrideChannel } from './prefsModel';

import { getFunctions, httpsCallable } from "firebase/functions";

const ICONS = {
  notification: "/assets/SVG-Icons/notification.svg",
};

const addTeamPrefixToNotificationText = (message, teamName) => {
  const trimmedMessage = typeof message === "string" ? message.trim() : "";
  if (!trimmedMessage || !teamName) return trimmedMessage;

  const labeledTeamName = `Team: ${teamName}`;
  if (trimmedMessage.includes(labeledTeamName)) {
    return trimmedMessage;
  }

  return trimmedMessage.includes(teamName)
    ? trimmedMessage.replace(teamName, labeledTeamName)
    : trimmedMessage;
};

export default function NotificationsDropdown({
  triggerMode = "click",
  label = "",
  containerClassName = "",
  buttonClassName = "",
  imageClassName = "",
  labelClassName = "",
  dropdownClassName = "",
}) {
  const [preferenceUid,setPreferenceUid]=useState(()=>getAuth().currentUser?.uid||null);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [discordEnabled, setDiscordEnabled] = useState(false);
  const [discordId, setDiscordId] = useState(null);      // track whether they've connected
  const [alert, setAlert] = useState(null);              // for CustomAlert
  const containerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [rawPrefs, setPrefs] = useState(defaultPrefs());
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const masterRef = useRef(null);
  const { teamId: activeTeamId } = useTeam();

  // 'global' or a concrete teamId
  const [scope, setScope] = useState('global');
  const presetContext=useNotificationContext(preferenceUid,scope==='global'?null:scope);
  const prefs=normalizePrefs(rawPrefs,presetContext);

  // cache separate prefs by scope so UI stays snappy when switching
  const [, setPrefsGlobal] = useState(defaultPrefs());
  const [, setPrefsTeam] = useState(defaultPrefs());
  const [, setTeamPrefsLoaded] = useState(false);

  // lazy cache for per-team prefs when notifications arrive from another team
  const teamPrefsCacheRef = useRef({}); // { [teamId]: prefsDocData }

  const [teamOptions, setTeamOptions] = useState([]); // [{id, name}]
  const hoverEnabled = triggerMode === "hover" || triggerMode === "both";

  // Firebase callable
  const functions = getFunctions();
  const respondEventParticipation = httpsCallable(functions, "respondEventParticipation");

  // robust requestId extraction (supports a few shapes)
  const getRequestIdFromNotif = (n) =>
    n?.requestId ||
    n?.payload?.requestId ||
    n?.payload?.id ||
    n?.payload?.request?.id ||
    null;

  const respondingRequests = useRef(new Set());
  // The server owns the recorded answer. Read receipts are independent.
  const handleParticipationResponse = async (notif, response) => {
    const user = getAuth().currentUser;
    const requestId = getRequestIdFromNotif(notif);
    const teamId = notif.teamId || notif?.payload?.teamId;
    if (!user || !requestId || !teamId) {
      showAlert("This request is unavailable. Please sign in and reopen notifications.", "error");
      return;
    }
    const key = `${teamId}/${requestId}`;
    if (respondingRequests.current.has(key)) return;
    respondingRequests.current.add(key);
    setNotes(prev => prev.map(n => n.id === notif.id ? { ...n, _responding: true } : n));
    try {
      const result = await respondEventParticipation({ teamId, requestId, response });
      const savedResponse = result.data?.status;
      if (savedResponse !== 'accepted' && savedResponse !== 'declined') throw new Error('Response not recorded');
      setNotes(prev => prev.map(n => n.id === notif.id
        ? { ...n, read: true, _responding: false, _responded: true, _respondedWith: savedResponse, response: savedResponse }
        : n));
      setReadState(notif.id, true).catch(() => {});
      showAlert(`Request ${savedResponse}.`, "success");
    } catch (error) {
      setNotes(prev => prev.map(n => n.id === notif.id ? { ...n, _responding: false } : n));
      showAlert(error?.code === 'functions/failed-precondition' ? 'This request is no longer current. Please use the latest confirmation request.' : "Could not send your response. Please try again.", "error");
    } finally {
      respondingRequests.current.delete(key);
    }
  };

  const getScopeDocRef = useCallback((user, targetScope = scope) => {
    if (!user) return null;
    // if not global, treat targetScope as teamId
    if (targetScope !== 'global') {
      return doc(db, 'users', user.uid, 'notifPrefs', targetScope);
    }
    return doc(db, 'users', user.uid, 'notifPrefs', 'global');
  }, [scope]);

  const switchActiveTeam = async (newTeamId) => {
    const user = getAuth().currentUser;
    if (!user) return showAlert('Please log in to change team.', 'error');
    try {
      await updateDoc(doc(db, 'users', user.uid), { activeTeamId: newTeamId || null });
      // TeamContext live-listener will update `activeTeamId`, and our effect will load that team’s prefs.
    } catch (e) {
      showAlert('Could not switch team. Please try again.', 'error');
    }
  };

  // Treat Discord as ON only if the per-type flag is true AND the
  // account has Discord enabled AND a discordId is present.
  const isEffectiveOn = useCallback((tKey, c) => {
    if (c !== 'discord') return isEnabled(prefs, tKey, c);
    return isEnabled(prefs, tKey, 'discord') && !!discordEnabled && !!discordId;
  }, [discordEnabled, discordId, prefs]);

  const areAllEffectiveOn = useCallback(() =>
    NOTIF_TYPES.every(({ key }) =>
      (NOTIF_TYPES.find(item=>item.key===key)?.noDiscord ? ['inApp','push'] : ['inApp', 'push', 'discord']).every((c) => isEffectiveOn(key, c))
    ), [isEffectiveOn]);

  const areAnyEffectiveOn = useCallback(() =>
    NOTIF_TYPES.some(({ key }) =>
      ['inApp', 'push', 'discord'].some((c) => isEffectiveOn(key, c))
    ), [isEffectiveOn]);


  // keep latest prefs available inside snapshot callback
  const latestPrefsRef = useRef(prefs);
  useEffect(() => {
    latestPrefsRef.current = prefs;
  }, [prefs]);

  // Live-listen to the current scope's prefs so changes on Account page and here stay in sync
  useEffect(() => {
    const user = getAuth().currentUser;
    if (!user) return;

    return subscribeNotificationPrefs(db,user.uid,scope,next=>{
      setPrefs(next);
      if(scope==='global')setPrefsGlobal(next);else teamPrefsCacheRef.current[scope]=next;
    });
  }, [scope]);


  // --- sorting helpers ---
  const getCreatedMs = (n) =>
    n?.createdAt?.toMillis
      ? n.createdAt.toMillis()
      : typeof n?.createdAt === 'number'
      ? n.createdAt
      : 0;

  // unread (read === false) should come first; then sort by createdAt desc
  const notifComparator = (a, b) => {
    if (!!a.read !== !!b.read) return a.read ? 1 : -1; // false (unread) comes first
    return getCreatedMs(b) - getCreatedMs(a);           // newest first within the group
  };

  // Keep master checkbox's indeterminate state in sync
  useEffect(() => {
    if (!masterRef.current) return;
    const allOn = areAllEffectiveOn();
    const anyOn = areAnyEffectiveOn();
    masterRef.current.indeterminate = !allOn && anyOn; // mixed state
  }, [prefs, discordEnabled, discordId, areAllEffectiveOn, areAnyEffectiveOn]);

  // Show UI alerts only for errors; log everything else to the console
  const showAlert = (message, type = 'info') => {
    const prefix = '[Notifications]';
    if (type === 'error') {
      setAlert({ message, type });
      setTimeout(() => setAlert(null), 5000);
    } else {
      // info/success/warn → console only
      if (type === 'success') console.log(prefix, message);
      else if (type === 'warn' || type === 'warning') console.warn(prefix, message);
      else console.log(prefix, message);
    }
  };

  // Subscribe to auth plus load notifications & Discord‐toggle pref
  useEffect(() => {
    const auth = getAuth();
    let unsubscribeNotifs = () => {};
    let stopUserSnap = () => {};

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      teamPrefsCacheRef.current = {};
      setPreferenceUid(user?.uid||null);
      // cleanup previous listeners
      unsubscribeNotifs();
      if (stopUserSnap) stopUserSnap();

      if (!user) {
        setNotes([]);
        setDiscordEnabled(false);
        setDiscordId(null);
        setPrefsGlobal(defaultPrefs());
        setPrefsTeam(defaultPrefs());
        setTeamPrefsLoaded(false);
        setPrefs(defaultPrefs());
        return;
      }

      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const root = userDoc.data() || {};

      // 1) Discord account-wide bits (unchanged)
      setDiscordEnabled(root.discordNotificationsEnabled ?? false);
      setDiscordId(root.discordId ?? null);

      // Load the user's teams so we can quick-switch activeTeamId from the dropdown
      const userTeamIds = Array.isArray(root.teamIds) ? root.teamIds : [];
      if (userTeamIds.length) {
        // Try to read each team doc. If we get permission-denied, skip it.
        const teamDocs = await Promise.all(
          userTeamIds.map(async (tid) => {
            try {
              return await getDoc(doc(db, "teams", tid));
            } catch (e) {
              // permission-denied / missing permissions => treat as invalid team
              return null;
            }
          })
        );

        const opts = teamDocs
          .map((snap, i) =>
            snap && snap.exists()
              ? { id: userTeamIds[i], name: snap.data().teamName || userTeamIds[i] }
              : null
          )
          .filter(Boolean);

        setTeamOptions(opts);

        // ✅ OPTIONAL BUT RECOMMENDED:
        // If some teamIds are no longer readable, remove them from the user's doc
        const readableIds = new Set(opts.map((t) => t.id));
        const staleIds = userTeamIds.filter((tid) => !readableIds.has(tid));

        if (staleIds.length) {
          try {
            const healedTeamIds = userTeamIds.filter((tid) => readableIds.has(tid));
            const healedActive =
              (root.activeTeamId && readableIds.has(root.activeTeamId))
                ? root.activeTeamId
                : (healedTeamIds[0] || null);

            await updateDoc(userRef, {
              teamIds: healedTeamIds,
              activeTeamId: healedActive,
            });
          } catch (err) {
            // ignore healing failures
          }
        }
      } else {
        setTeamOptions([]);
      }

      const globalSnap=await getDoc(doc(db,'users',user.uid,'notifPrefs','global'));
      const globalPrefs=serializePrefs(globalSnap.data()||root.notificationPrefs||{version:2});
      setPrefsGlobal(globalPrefs);

      // 5) Listen to in-app notification list, like before
      const notifsRef = collection(db, 'users', user.uid, 'notifications');
      const q = query(notifsRef, orderBy('createdAt', 'desc'));
      let initialNotifications=true;
      unsubscribeNotifs = onSnapshot(q, async (snap) => {
        const docsList = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter(n=>n.channels?.inApp!==false);

        // ✅ Preserve local UI-only flags so actions don't pop back in during refresh
        setNotes((prev) => {
          const prevById = new Map(prev.map((p) => [p.id, p]));
          const merged = docsList.map((n) => {
            const old = prevById.get(n.id);
            if (!old) return n;
            return {
              ...n,
              _responded: old._responded || n.responded || !!n.response || !!n.respondedAt,
              _respondedWith: n.response || old._respondedWith || null,
              _responding: old._responding && !n.response,
            };
          });
          merged.sort(notifComparator);
          return merged;
        });

        if(initialNotifications){initialNotifications=false;return;}
        // push notifications: respect per-team prefs if notification.teamId is present
        for (const change of snap.docChanges()) {
          if (change.type !== 'added') continue;
          const n = change.doc.data();
          const nType = n.type;

          // figure out which scope applies
          let usePrefs = null;
          const notificationTeamId=n.teamId || n.payload?.teamId;
          if (notificationTeamId) {
            // use cached team prefs or fetch once
            if (!teamPrefsCacheRef.current[notificationTeamId]) {
              const ref = doc(db, 'users', user.uid, 'notifPrefs', notificationTeamId);
              const s = await getDoc(ref);
              if (s.exists()) teamPrefsCacheRef.current[notificationTeamId] = s.data();
              else {
                teamPrefsCacheRef.current[notificationTeamId] = globalPrefs;
              }
            }
            usePrefs = teamPrefsCacheRef.current[notificationTeamId];
          } else {
            usePrefs = latestPrefsRef.current || globalPrefs;
          }

          const pushAllowed =
            (usePrefs?.masterEnabled ?? true) &&
            (n.channels?.push===true?usePrefs?.overrides?.[nType]?.push!==false:isEnabled(usePrefs,nType,'push'));

          if (!pushAllowed || n.channels?.push===false) continue;

          if (Notification.permission === 'granted') {
            new Notification(n.message);
          } else if (Notification.permission !== 'denied') {
            const perm = await Notification.requestPermission();
            if (perm === 'granted') new Notification(n.message);
          }
        }
      });

      // live listen to the root user doc (for discord toggle and activeTeam changes)
      stopUserSnap = onSnapshot(userRef, (snap) => {
        const d = snap.data() || {};
        setDiscordEnabled(d.discordNotificationsEnabled ?? false);
        setDiscordId(d.discordId ?? null);
      });
    });

    return () => {
      unsubscribeAuth();
      unsubscribeNotifs();
      if (stopUserSnap) stopUserSnap();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTeamId, scope]);

  useEffect(() => {
    // choose a sensible default when a team is active
    if (activeTeamId) setScope((s) => (s === 'global' ? activeTeamId : s));
  }, [activeTeamId]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);


  // close on outside click
  useEffect(() => {
    const onClick = e => {
      if (open && containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleHoverOpen = () => {
    if (!hoverEnabled) return;
    clearCloseTimer();
    setOpen(true);
  };

  const handleHoverClose = () => {
    if (!hoverEnabled) return;
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, 120);
  };

  const handleTriggerClick = () => {
    clearCloseTimer();
    if (triggerMode === "hover") {
      setOpen(true);
      return;
    }

    setOpen((current) => !current);
  };

  const toggleMaster = async (enabled) => {
    const user = getAuth().currentUser;
    if (!user) return showAlert('Please log in to change preferences.', 'error');

    setSavingPrefs(true);
    try {
      const ref = getScopeDocRef(user);
      await setDoc(ref, serializePrefs({...prefs, masterEnabled: enabled}));

      // ✅ compute next once, store consistently everywhere
      setPrefs((prev) => {
        const next = { ...prev, masterEnabled: enabled };

        if (scope === 'global') setPrefsGlobal(next);
        else teamPrefsCacheRef.current[scope] = next;

        return next;
      });

      showAlert(
        `Notifications ${enabled ? 'enabled' : 'disabled'} for ${
          scope === 'global' ? 'your account' : 'this team'
        }.`,
        'success'
      );
    } catch (e) {
      showAlert('Could not update notification setting. Please try again.', 'error');
    } finally {
      setSavingPrefs(false);
    }
  };


  // Toggle Discord‐DMs preference
  const toggleDiscord = async () => {
    const user = getAuth().currentUser;
    if (!user) {
      showAlert('Please log in to enable Discord DMs.', 'error');
      return;
    }
    if (!discordId) {
      showAlert(
        'Please go to your Account page and connect your Discord (and join our server) to receive DMs.',
        'info'
      );
      return;
    }
    // now actually flip the bit
    const userRef = doc(db, 'users', user.uid);
    await updateDoc(userRef, {
      discordNotificationsEnabled: !discordEnabled,
    });
    setDiscordEnabled(e => !e);
  };

  const updatePref = async (typeKey, channel, value) => {
    const user = getAuth().currentUser;
    if (!user) return showAlert('Please log in to change preferences.', 'error');
    setSavingPrefs(true);
    try {
      const next=overrideChannel(prefs,typeKey,channel,value,presetContext);
      setPrefs(next);

      if (scope === 'global') setPrefsGlobal(next);
      else teamPrefsCacheRef.current[scope] = next;

      const ref = getScopeDocRef(user);
      await setDoc(ref, serializePrefs(next))

      showAlert('Notification preference updated.', 'success');
    } catch (e) {
      showAlert('Could not save preference. Please try again.', 'error');
    } finally {
      setSavingPrefs(false);
    }
  };


  // Toggle read state (also does optimistic UI so it feels instant)
  const setReadState = async (id, nextRead) => {
    const user = getAuth().currentUser;
    if (!user) return;

    // optimistic UI
    setNotes((prev) => {
      const next = prev.map(n => (n.id === id ? { ...n, read: nextRead } : n));
      next.sort(notifComparator);
      return next;
    });


    await updateDoc(doc(db, 'users', user.uid, 'notifications', id), {
      read: nextRead,
    });
  };

  const markAllAsRead = async () => {
    const user = getAuth().currentUser;
    if (!user) return showAlert('Please log in to change notifications.', 'error');

    const unread = notes.filter(n => !n.read);
    if (unread.length === 0) return;

    try {
      // optimistic UI first
      setNotes((prev) => {
        const next = prev.map(n => (n.read ? n : { ...n, read: true }));
        next.sort(notifComparator);
        return next;
      });

      const batch = writeBatch(db);
      unread.forEach(n => {
        batch.update(doc(db, 'users', user.uid, 'notifications', n.id), { read: true });
      });
      await batch.commit();
      showAlert('All notifications marked as read.', 'success');
    } catch (e) {
      showAlert('Could not mark all as read. Please try again.', 'error');
    }
  };

  const currentUser = getAuth().currentUser;
  const unreadCount = notes.reduce((count, notification) => count + (notification.read ? 0 : 1), 0);
  const getNotificationTeamName = (notification) => {
    const notificationTeamId =
      notification?.teamId ||
      notification?.payload?.teamId ||
      null;
    if (!notificationTeamId) {
      return null;
    }
    return teamOptions.find((team) => team.id === notificationTeamId)?.name || null;
  };

  return (
    <>
      {alert && (
        <CustomAlert
          message={alert.message}
          type={alert.type}
          onClose={() => setAlert(null)}
          position="top"
        />
      )}

      <div
        className={`notification-container ${containerClassName}`.trim()}
        ref={containerRef}
        onMouseEnter={handleHoverOpen}
        onMouseLeave={handleHoverClose}
      >
        <button
          className={`notification-icon ${buttonClassName}`.trim()}
          onClick={handleTriggerClick}
          aria-label="Notifications"
          aria-expanded={open}
          aria-haspopup="menu"
          type="button"
        >
          <span className="notification-iconGlyph" aria-hidden="true">
            <SidebarNotificationImage
              src={ICONS.notification}
              alt=""
              className={`topbar-svg-icon notification-icon-img ${imageClassName}`.trim()}
              loading="eager"
              decoding="async"
            />
            {unreadCount > 0 && (
              <span className="badge">
                {unreadCount}
              </span>
            )}
          </span>
          {label ? (
            <span className={`notification-trigger-label ${labelClassName}`.trim()}>
              {label}
            </span>
          ) : null}
        </button>


        {open && (
          <div className={`notification-dropdown ${dropdownClassName}`.trim()}>
            { !currentUser ? (
              // not logged in
              <div className="notification-item">
                Please login to enjoy this function
              </div>
            ) : (
              <>
                <div className="notification-item prefs-header">
                  <span className="prefs-header-title">Notifications</span>

                  <div className="prefs-header-actions">
                    
                    {savingPrefs && <span className="prefs-saving">Saving…</span>}

                    <button
                      type="button"
                      className="mark-all-btn"
                      onClick={markAllAsRead}
                      title="Mark all as read"
                      disabled={unreadCount === 0}
                    >
                      Mark all read
                    </button>
                    
                    <label className="master-toggle">
                      <input
                        ref={masterRef}
                        type="checkbox"
                        checked={!!prefs.masterEnabled}
                        onChange={(e) => toggleMaster(e.target.checked)}
                      />
                      <span className="master-toggle-label">
                        {prefs.masterEnabled ? 'On' : 'Off'}
                      </span>
                    </label>

                    <button
                      className="gear-btn"
                      type="button"
                      aria-label={showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
                      onClick={() => setShowAdvanced((s) => !s)}
                      title={showAdvanced ? 'Hide advanced' : 'Advanced settings'}
                    >
                      ⚙️
                    </button>
                  </div>
                </div>

                {showAdvanced && (
                 <>
                    <div className="notification-item discord-toggle">
                     <label>
                       <input
                         type="checkbox"
                         checked={discordEnabled}
                         onChange={toggleDiscord}
                       />{' '}
                       Enable Discord DMs (account-wide)
                     </label>
                   </div>

                   {/* Scope picker (moved under the gear panel) */}
                    <div className="notification-item">
                      <div className="scope-switch" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <label htmlFor="notif-scope">Apply settings for:</label>
                        <select
                          id="notif-scope"
                          value={scope}
                          onChange={async (e) => {
                            const val = e.target.value;
                            setScope(val);
                            if (val !== 'global') {
                              await switchActiveTeam(val);
                            }
                          }}
                          aria-label="Notification settings scope"
                        >
                          <option value="global">Global (account-wide)</option>
                          {teamOptions.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>


                   <NotificationPresets prefs={prefs} context={presetContext} scope={scope} onChange={async next=>{try{await setDoc(getScopeDocRef(getAuth().currentUser),serializePrefs(next));setPrefs(next);}catch{showAlert('Could not save presets. Please retry.','error');}}}/>
<CalendarCoverageSetting prefs={prefs} onChange={async value=>{const user=getAuth().currentUser;if(!user)return;await setDoc(doc(db,'users',user.uid,'notifPrefs',scope),serializePrefs({...prefs,followTeamEvents:value}));}}/>
<PastEventChangesSetting prefs={prefs} onChange={async value=>{const user=getAuth().currentUser;if(!user)return;await setDoc(doc(db,'users',user.uid,'notifPrefs',scope),serializePrefs({...prefs,includePastEventChanges:value}));}}/>
<div className="notification-item prefs-legend">
                     <span className="legend-type">Type</span>
                     <span className="legend-col">Website Notification dropdown</span>
                     <span className="legend-col">Website Push Notification</span>
                     <span className="legend-col">Discord dm</span>
                   </div>
              
                    {NOTIF_TYPES.map(({ key, label, noDiscord, group }, index) => (
                      <div key={key} className="notification-item prefs-row" role="group" aria-label={`${label} preferences`}>
                        {(index===0 || NOTIF_TYPES[index-1].group!==group) && <strong className="notification-group-title">{group}</strong>}
          <span className="pref-type-label">{label}</span>
                        <label className="pref-check">
                          <input
                            type="checkbox" checked={isEnabled(prefs, key, 'inApp')}
                            onChange={(e) => updatePref(key, 'inApp', e.target.checked)}
                          />
                        </label>
                        <label className="pref-check">
                          <input
                            type="checkbox" checked={isEnabled(prefs, key, 'push')}
                            onChange={(e) => updatePref(key, 'push', e.target.checked)}
                          />
                        </label>
                        <label className="pref-check">
                          <input
                            type="checkbox" checked={!noDiscord && isEnabled(prefs, key, 'discord') && discordEnabled} disabled={noDiscord}
                            onChange={async (e) => {
                              if (!discordId && e.target.checked) {
                                showAlert('Connect Discord on the Account page to receive DMs.', 'info');
                                return;
                              }
                              if (!discordEnabled && e.target.checked) {
                                await toggleDiscord();
                              }
                              updatePref(key, 'discord', e.target.checked);
                            }}
                          />
                        </label>
                      </div>
                    ))}
                  </>
               )}
                {notes.length === 0 && (
                  <div className="notification-item">No notifications</div>
                )}
                {notes.map((n) => {
                  const isParticipationReq = n.type === "eventParticipationRequest";
                  const isDigest = n.type === 'weeklyDigest' && Array.isArray(n.payload?.digestRows);
                  const isCalendarEvent = ['eventAdded', 'eventEdited', 'eventRemoved', 'eventCancelled', 'eventReminder',
                    'eventParticipationResponse', 'eventParticipationReminder'].includes(n.type);
                  const teamName = getNotificationTeamName(n);
                  const displayMessage = n.type === "rosterExpectations" ? n.message : addTeamPrefixToNotificationText(n.message, teamName);
                  const navigateNotification = async route => {
                    try {
                      await setReadState(n.id, true);
                      const teamId = n.teamId || n.payload?.teamId;
                      if (teamId) await switchActiveTeam(teamId);
                      setOpen(false); window.location.href = route;
                    } catch {showAlert('Could not open this section. Please try again.', 'error');}
                  };

                  return (
                    <div
                      key={n.id}
                      className={`notification-item notification-entry ${n.read ? "read" : "unread"}`}
                      onClick={async () => {
                        // Requests use explicit response and View event controls.
                        if (isParticipationReq || isDigest) return;

                        setOpen(false);
                        await setReadState(n.id, true);
                        if (n.payload?.url) window.location.href = n.payload.url;
                      }}
                    >
                      <button
                        className={`status-btn ${n.read ? "read" : "unread"}`}
                        type="button"
                        aria-label={n.read ? "Mark as unread" : "Mark as read"}
                        title={n.read ? "Mark as unread" : "Mark as read"}
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          await setReadState(n.id, !n.read);
                        }}
                      >
                        ✓
                      </button>

                      <div className="notification-content">
                        {isDigest ? <WeeklyDigestContent notification={n} teamName={teamName} onNavigate={navigateNotification}/> :
                          isParticipationReq ? <ParticipationRequestContent notification={n} onRespond={handleParticipationResponse} onNavigate={navigateNotification}/> :
                          isCalendarEvent ? <EventNotificationContent message={displayMessage} url={n.payload?.url}
                            cancelled={n.type === 'eventCancelled' || displayMessage.includes('❌ Event cancelled')} onNavigate={navigateNotification}/> :
                            <span className="notification-message">{displayMessage}</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function SidebarNotificationImage(props) {
  return props.className?.includes("New-Sidebar__") ? <SidebarSvgImage {...props}/> : <img {...props} alt=""/>;
}
